import React, { useState, useEffect } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { motion } from 'framer-motion';

interface AnimatedLoaderProps {
  message?: string;
  duration?: number;
  fastLoad?: boolean; // New prop for auth pages to load faster
}

const AnimatedLoader: React.FC<AnimatedLoaderProps> = ({ 
  message = 'Loading...', 
  duration = 3000,
  fastLoad = false
}) => {
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(message);
  
  // Use shorter intervals for fast loading mode
  const interval = fastLoad ? 20 : 50;
  const actualDuration = fastLoad ? Math.min(duration, 1000) : duration;

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + (100 / (actualDuration / interval));
        
        // Update messages at certain thresholds
        if (newProgress >= 90 && !fastLoad) {
          setCurrentMessage('Almost there...');
        } else if (newProgress >= 60 && !fastLoad) {
          setCurrentMessage('Finalizing...');
        } else if (newProgress >= 30 && !fastLoad) {
          setCurrentMessage('Processing...');
        }
        
        return newProgress >= 100 ? 100 : newProgress;
      });
    }, interval);
    
    // Clear interval when component unmounts or duration is reached
    const cleanup = setTimeout(() => {
      clearInterval(timer);
      setProgress(100);
    }, actualDuration);
    
    return () => {
      clearInterval(timer);
      clearTimeout(cleanup);
    };
  }, [actualDuration, interval, fastLoad]);

  // Logo animation variants
  const logoVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.8
    },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  // Circle animation variants
  const circleVariants = {
    animate: {
      rotate: 360,
      transition: {
        duration: 2.5, // Slightly slower for better visibility
        ease: "linear",
        repeat: Infinity
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        {/* AkMo Logo with Circular Spinner */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={logoVariants}
          style={{ position: 'relative', width: '120px', height: '120px', marginBottom: '20px' }}
        >
          {/* Rotating circular border */}
          <motion.div
            animate="animate"
            variants={circleVariants}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              border: '6px solid transparent', 
              borderTopColor: '#4763E4',    // Blue (color a)
              borderRightColor: '#8367F7',  // Purple (color b)
              borderBottomColor: '#4763E4', // Blue (color a)
              borderLeftColor: '#8367F7',   // Purple (color b)
              boxSizing: 'border-box',
              boxShadow: '0 0 10px rgba(111, 99, 247, 0.5)' 
            }}
          />

          {/* Second rotating border (opposite direction) for enhanced effect */}
          <motion.div
            animate={{ 
              rotate: -360,
              transition: {
                duration: 3,
                ease: "linear",
                repeat: Infinity
              }
            }}
            style={{
              position: 'absolute',
              top: '5px',
              left: '5px',
              right: '5px',
              bottom: '5px',
              borderRadius: '50%',
              border: '3px dotted transparent', 
              borderTopColor: 'rgba(131, 103, 247, 0.3)',    // Purple (faded)
              borderBottomColor: 'rgba(71, 99, 228, 0.3)',   // Blue (faded)
              boxSizing: 'border-box'
            }}
          />

          {/* Logo in center */}
          <Box sx={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '84px',
            height: '84px',
            backgroundColor: '#f0f4f8',
            borderRadius: '50%',
            boxShadow: 'inset 0 0 5px rgba(0,0,0,0.1)'
          }}>
            <img 
              src="/assets/akmobooks.com.svg"
              alt="AkMo Logo"
              style={{
                height: '50px',
                width: 'auto'
              }}
            />
          </Box>
        </motion.div>
        
        <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#4763E4' }}>
          {currentMessage}
        </Typography>
        <Box sx={{ width: '300px', mt: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              backgroundColor: '#e0e7ff',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
                backgroundImage: 'linear-gradient(90deg, #4763E4, #8367F7)'
              }
            }}
          />
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            mt: 0.5,
            fontSize: '0.75rem', 
            color: 'text.secondary'
          }}>
            {`${Math.round(progress)}%`}
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};

export default AnimatedLoader; 